/* eslint-disable max-lines-per-function */
/* eslint-disable react/jsx-no-useless-fragment */
import React, { useEffect } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import { CircularProgress, Grid, IconButton, Typography } from '@mui/material'
import { StateMethods, useHookstate } from '@hookstate/core'
import CloseIcon from '@mui/icons-material/Close'
import { useLang } from '~/hooks/useLang'
import dayjs from 'dayjs'
import { getById } from './services'
import { mountUrlRequest } from '~/services/fetch'

export interface ViewProcedureProps {
  readonly model: string
  readonly viewTitle: string
  readonly open: StateMethods<boolean>
  readonly infoId: number | null
  readonly handleClose: () => void
  readonly showInViewMore: readonly string[]
  readonly booleanField?: readonly string[]
  readonly codeFields?: readonly string[]
  readonly cellLabel?: readonly { readonly field: string; readonly value: any }[]
  readonly numberField?: readonly string[]
  readonly customViewMoreComponent?: React.ComponentType<any>
}

export default function CrudViewMore({
  model,
  open,
  infoId,
  handleClose,
  showInViewMore,
  viewTitle,
  booleanField,
  codeFields,
  cellLabel,
  numberField,
  customViewMoreComponent: CustomViewMore,
}: ViewProcedureProps) {
  const { translate } = useLang()
  const infos = useHookstate<readonly any[]>([])
  const loading = useHookstate<boolean>(false)

  // ignore exhaustive-deps: we only want to fetch when infoId or model changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Só busca dados se não houver componente customizado
    if (!CustomViewMore) {
      loading.set(true)
      if (infoId) {
        getById(model, infoId)
          .then((data) => {
            infos.set(data)
          })
          .finally(() => loading.set(false))
      }
    }
  }, [infoId, model, CustomViewMore])

  // Se houver componente customizado, use-o
  if (CustomViewMore) {
    return (
      <CustomViewMore
        open={open}
        itemId={infoId}
        handleClose={handleClose}
      />
    )
  }

  const getLabel = {
    'area_plan:name': 'Area Plan',
    'city:name': 'City',
    'state:name': 'State',
    'vehicle:identification': 'Vehicle Identification',
    'vehicle:plate': 'Plate',
    'operation_unit:name': 'Operation unit',
    dispatch_groups: 'Dispatch Group',
    'specie:name': 'Specie',
    'weapon_type:name': 'Tipo de armamento',
    birth_date: 'Birth Date',
    blood_type: 'Blood Type',
    'rank:name': 'Patent',
    nickname: 'War_name',
    login: 'User Login',
    groups: 'Permission Groups',
    cpf: 'CPF',
    area_plan: 'Mapa',
    start_date: 'Data Inicial',
    end_date: 'Data Final',
    'created_by_user:representation_name': 'Criado por',
    updated_at: 'Atualizado em',
    created_at: 'Criado em',
    'commander:representation_name': 'Commander',
    'driver:representation_name': 'Driver',
    patrolmans: 'Patrolmans',
    must_inform_consummate: 'Must Inform Consummate',
    ref_to_vehicle: 'Referente a Veículo',
    // equipment-specific labels
    serial_number: 'serial_number',
    'equipment_group:name': 'Equipment Group',
    'warehouse:name': 'Warehouse',
    is_active: 'Active',
    total_items: 'Total Items',
    warehouse_users: 'Managers',
    mandatory_vehicle: 'Qtd. Veículo',
    mandatory_involved: 'Qtd. Envolvido',
    mandatory_weapon: 'Qtd. Arma',
    mandatory_organization: 'Qtd. Organização',
    mandatory_drug: 'Qtd. Droga',
    mandatory_object: 'Qtd. Objetos',
    mandatory_animal: 'Qtd. Animal',
  }

  const getCellLabel = (item: string) => {
    if (!cellLabel) return undefined
    const cell = cellLabel.find((c) => c.field === item)
    if (!cell) return undefined

    const raw = infos[item]?.get()

    // direct key
    if (cell.value[raw] !== undefined && cell.value[raw] !== null) return cell.value[raw]
    // string key
    if (cell.value[String(raw)] !== undefined && cell.value[String(raw)] !== null) return cell.value[String(raw)]

    // boolean fallback
    if (typeof raw === 'boolean') {
      const key = raw ? 1 : 0
      if (cell.value[key] !== undefined && cell.value[key] !== null) return cell.value[key]
      if (cell.value[String(key)] !== undefined && cell.value[String(key)] !== null) return cell.value[String(key)]
    }

    // number fallback
    if (typeof raw === 'number') {
      if (cell.value[raw] !== undefined && cell.value[raw] !== null) return cell.value[raw]
      if (cell.value[String(raw)] !== undefined && cell.value[String(raw)] !== null) return cell.value[String(raw)]
    }

    return undefined
  }

  const clickOutModalClose = (event, reason) => {
    if (reason !== 'backdropClick') {
      open.set(false)
    }
  }

  const isDateField = (field: string) => {
    const dateFields = [
      'created_at',
      'birth_date',
      'start_date',
      'end_date',
      'Updated_at',
    ]
    return dateFields.includes(field)
  }

  const renderInspectionView = () => {
    const records = infos['inspection_item_records']?.get() ?? []
    const okItems = records
      .filter((rec) => rec.item_status === 1)
      .map((rec) => rec.inspection_item?.name)
    const notOkItems = records
      .filter((rec) => rec.item_status === 2)
      .map((rec) => rec.inspection_item?.name)
    const undefinedItems = records
      .filter((rec) => rec.item_status === 3)
      .map((rec) => rec.inspection_item?.name)
    const inoperative = records.filter(
      (rec) => rec.item_status === 4 || rec.notes || rec.media_path
    )

    return (
      <Grid container xs={12} display={'flex'} flexDirection={'column'}>
        <Grid
          container
          xs={12}
          display={'flex'}
          flexDirection={'row'}
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Grid item xs={3}>
            <Typography variant="subtitle1" fontWeight={'bold'}>
              Dispositivo:
            </Typography>
            <Typography variant="subtitle2">
              {infos['device']?.get()?.name ?? translate('Uninformed')}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="subtitle1" fontWeight={'bold'}>
              {translate('Task') + ':'}
            </Typography>
            <Typography variant="subtitle2">
              {infos['task']?.get()
                ? infos['task'].get()
                : infos['is_operational']?.get()
                ? translate('Sim')
                : translate('Não')}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="subtitle1" fontWeight={'bold'}>
              Grupo de Despacho:
            </Typography>
            <Typography variant="subtitle2">
              {infos['dispatch_group']?.get()?.name ?? translate('Uninformed')}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="subtitle1" fontWeight={'bold'}>
              Data de Vistoria:
            </Typography>
            <Typography variant="subtitle2">
              {infos['created_at']?.get()
                ? dayjs(infos['created_at'].get()).format('DD/MM/YYYY HH:mm:ss')
                : translate('Uninformed')}
            </Typography>
          </Grid>
        </Grid>

        {infos['commander']?.get() && (
          <Grid item xs={12} sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={'bold'}>
              Comandante
            </Typography>
            <Typography variant="subtitle2">
              {infos['commander']?.get()?.representation_name ??
                infos['commander']?.get()?.name}
              {infos['commander']?.get()?.phone
                ? ` - Contato: ${infos['commander'].get().phone}`
                : ''}
            </Typography>
          </Grid>
        )}

        <Grid item xs={12} sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={'bold'}>
            EM CONFORMIDADE:
          </Typography>
          <Typography variant="subtitle2">
            {okItems.length ? okItems.join(', ') : translate('Uninformed')}
          </Typography>
          <br />
          <Typography variant="subtitle1" fontWeight={'bold'}>
            INCONFORMIDADE:
          </Typography>
          <Typography variant="subtitle2">
            {notOkItems.length
              ? notOkItems.join(', ')
              : translate('Uninformed')}
          </Typography>
          <br />
          <Typography variant="subtitle1" fontWeight={'bold'}>
            NÃO VISTORIADO:
          </Typography>
          <Typography variant="subtitle2">
            {undefinedItems.length
              ? undefinedItems.join(', ')
              : translate('Uninformed')}
          </Typography>
          <br />
          {inoperative.length > 0 && (
            <>
              <Typography
                variant="subtitle1"
                fontWeight={'bold'}
                sx={{ mt: 2 }}
              >
                INOPERANTE
              </Typography>
              <Grid
                container
                sx={{
                  border: '1px solid rgba(0,0,0,0.2)',
                  borderRadius: 2,
                  p: 1,
                  maxHeight: 260,
                  overflowY: 'auto',
                }}
              >
                {inoperative.map((record, idx) => (
                  <Grid item xs={12} key={idx} sx={{ mb: 1 }}>
                    <Grid container>
                      <Grid item xs={6}>
                        <Typography variant="subtitle2">
                          <strong>
Item:
</strong>
{' '}
                          {record.inspection_item?.name ??
                            translate('Uninformed')}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="subtitle2">
                          <strong>
Justificativa:
</strong>
{' '}
                          {record.notes ?? translate('Uninformed')}
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid container sx={{ mt: 1 }}>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2">
                          <strong>
Anexo:
</strong>
                        </Typography>
                      </Grid>
                      {record.media_path ? (
                        <Grid item xs={12} sx={{ mt: 1 }}>
                          <img
                            src={mountUrlRequest(
                              `/inspection/get-media/${record.media_path}`
                            )}
                            alt="Anexo"
                            style={{ maxWidth: '100%', borderRadius: 4 }}
                          />
                        </Grid>
                      ) : (
                        <Grid item xs={12} sx={{ mt: 1 }}>
                          <Typography variant="subtitle2">
                            {translate('Uninformed')}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </Grid>
      </Grid>
    )
  }

  return (
    <Dialog
      fullWidth
      maxWidth={'md'}
      open={open.get()}
      onClose={clickOutModalClose}
      disableEscapeKeyDown={true}
    >
      <DialogTitle
        display={'flex'}
        alignItems={'center'}
        justifyContent={'space-between'}
      >
        {translate(viewTitle)}
        <IconButton aria-label="close" onClick={() => handleClose()}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading.get() ? (
          <Grid item xs={12} display={'flex'} sx={{ justifyContent: 'center' }}>
            <CircularProgress />
          </Grid>
        ) : model === 'inspection' ? (
          renderInspectionView()
        ) : (
          <Grid container xs={12} display={'flex'} flexDirection={'row'}>
            {showInViewMore.map((item, index) => {
              if (isDateField(item)) {
                return (
                  <Grid item xs={3} key={index} sx={{ pr: 1, pt: 2 }}>
                    <Typography variant="subtitle1" fontWeight={'bold'}>
                      {getLabel[item]
                        ? translate(getLabel[item])
                        : translate(item)}
                      :
                    </Typography>
                    <Typography variant="subtitle2">
                      {infos[item]?.get()
                        ? dayjs(infos[item]?.get()).format('DD/MM/YYYY HH:mm')
                        : translate('Uninformed')}
                    </Typography>
                  </Grid>
                )
              } else if (item == 'birth_date') {
                return (
                  <Grid item xs={3} key={index} sx={{ pr: 1, pt: 2 }}>
                    <Typography variant="subtitle1" fontWeight={'bold'}>
                      {getLabel[item]
                        ? translate(getLabel[item])
                        : translate(item)}
                      :
                    </Typography>
                    <Typography variant="subtitle2">
                      {infos[item]?.get()
                        ? getLabel[item] === 'Data Inicial' ||
                          getLabel[item] === 'Data Final'
                          ? dayjs(infos[item]?.get()).format(
                              'DD/MM/YYYY HH:mm:ss'
                            )
                          : dayjs(infos[item]?.get()).format('DD/MM/YYYY')
                        : translate('Uninformed')}
                    </Typography>
                  </Grid>
                )
              } else if (codeFields && codeFields.includes(item)) {
                return (
                  <Grid item xs={3} key={index} sx={{ pr: 1, pt: 2 }}>
                    <Typography variant="subtitle1" fontWeight={'bold'}>
                      {getLabel[item]
                        ? translate(getLabel[item])
                        : translate(item[0].toUpperCase() + item.slice(1))}
                      :
                    </Typography>
                    <Typography variant="subtitle2">
                      {infos[item]?.get()
                        ? `[${infos[item].get().code}] ${
                            infos[item].get().name
                          }`
                        : translate('Uninformed')}
                    </Typography>
                  </Grid>
                )
              } else if (Array.isArray(infos[item]?.get())) {
                return (
                  <Grid item xs={3} key={index} sx={{ pr: 1, pt: 2 }}>
                    <Typography variant="subtitle1" fontWeight={'bold'}>
                      {getLabel[item]
                        ? translate(getLabel[item])
                        : translate(item[0].toUpperCase() + item.slice(1))}
                      :
                    </Typography>
                    <Typography variant="subtitle2">
                      {showInViewMore.includes('can_manage_any_agency') &&
                      infos['can_manage_any_agency'].get()
                        ? translate('all')
                        : infos[item]?.get().length
                        ? infos[item]
                            ?.get()
                            .map((info) =>
                              info.name ? info.name : info.representation_name
                            )
                            .join(', ')
                        : translate('Uninformed')}
                    </Typography>
                  </Grid>
                )
              } else {
                const infoDependent = item.split(':')

                return (
                  <Grid item xs={3} key={index} sx={{ pr: 1, pt: 2 }}>
                    <Typography variant="subtitle1" fontWeight={'bold'}>
                      {getLabel[item]
                        ? translate(getLabel[item])
                        : translate(item[0].toUpperCase() + item.slice(1))}
                      :
                    </Typography>
                    <Typography variant="subtitle2">
                      {infoDependent?.length > 1
                        ? infos[infoDependent[0]]?.[infoDependent[1]]?.get()
                          ? infos[infoDependent[0]]?.[infoDependent[1]]?.get()
                          : translate('Uninformed')
                        : booleanField?.includes(item)
                        ? infos[item]?.get()
                          ? 'Sim'
                          : 'Não'
                        : getCellLabel(item) !== undefined
                        ? getCellLabel(item)
                        : numberField?.includes(item)
                        ? infos[item]?.get() ?? 0
                        : infos[item]?.get()
                        ? infos[item]?.get()
                        : translate('Uninformed')}
                    </Typography>
                  </Grid>
                )
              }
            })}
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  )

  return (
    <Dialog
      fullWidth
      maxWidth={'md'}
      open={open.get()}
      onClose={clickOutModalClose}
      disableEscapeKeyDown={true}
    >
      <DialogTitle
        display={'flex'}
        alignItems={'center'}
        justifyContent={'space-between'}
      >
        {translate(viewTitle)}
        <IconButton aria-label="close" onClick={() => handleClose()}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading.get() ? (
          <Grid item xs={12} display={'flex'} sx={{ justifyContent: 'center' }}>
            <CircularProgress />
          </Grid>
        ) : model === 'inspection' ? (
          <Grid container xs={12} display={'flex'} flexDirection={'column'}>
            {/* Header block: Device, Atuação, Grupo de Despacho, Data de Vistoria */}
            <Grid
              container
              xs={12}
              display={'flex'}
              flexDirection={'row'}
              spacing={2}
              sx={{ mb: 2 }}
            >
              <Grid item xs={3}>
                <Typography variant="subtitle1" fontWeight={'bold'}>
                  Dispositivo:
                </Typography>
                <Typography variant="subtitle2">
                  {infos['device']?.get()?.name ?? translate('Uninformed')}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="subtitle1" fontWeight={'bold'}>
                  {translate('Task') + ':'}
                </Typography>
                <Typography variant="subtitle2">
                  {infos['task']?.get()
                    ? infos['task'].get()
                    : infos['is_operational']?.get()
                    ? translate('Sim')
                    : translate('Não')}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="subtitle1" fontWeight={'bold'}>
                  Grupo de Despacho:
                </Typography>
                <Typography variant="subtitle2">
                  {infos['dispatch_group']?.get()?.name ??
                    translate('Uninformed')}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="subtitle1" fontWeight={'bold'}>
                  Data de Vistoria:
                </Typography>
                <Typography variant="subtitle2">
                  {infos['created_at']?.get()
                    ? dayjs(infos['created_at'].get()).format(
                        'DD/MM/YYYY HH:mm:ss'
                      )
                    : translate('Uninformed')}
                </Typography>
              </Grid>
            </Grid>

            {/* Commander block */}
            {infos['commander']?.get() && (
              <Grid item xs={12} sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={'bold'}>
                  Comandante
                </Typography>
                <Typography variant="subtitle2">
                  {infos['commander']?.get()?.representation_name ??
                    infos['commander']?.get()?.name}
                  {infos['commander']?.get()?.phone
                    ? ` - Contato: ${infos['commander'].get().phone}`
                    : ''}
                </Typography>
              </Grid>
            )}

            {/* Grouped inspection items */}
            <Grid item xs={12} sx={{ mb: 2 }}>
              {/* Extract items */}
              {(() => {
                const records = infos['inspection_item_records']?.get() ?? []
                const ok = records
                  .filter((r) => r.item_status === 1)
                  .map((r) => r.inspection_item?.name)
                const notOk = records
                  .filter((r) => r.item_status === 2)
                  .map((r) => r.inspection_item?.name)
                const undefinedItems = records
                  .filter((r) => r.item_status === 3)
                  .map((r) => r.inspection_item?.name)
                const inoperative = records.filter(
                  (r) => r.item_status === 4 || r.notes || r.media_path
                )
                return (
                  <>
                    <Typography variant="subtitle1" fontWeight={'bold'}>
                      EM CONFORMIDADE:
                    </Typography>
                    <Typography variant="subtitle2">
                      {ok.length ? ok.join(', ') : translate('Uninformed')}
                    </Typography>
                    <br />
                    <Typography variant="subtitle1" fontWeight={'bold'}>
                      INCONFORMIDADE:
                    </Typography>
                    <Typography variant="subtitle2">
                      {notOk.length
                        ? notOk.join(', ')
                        : translate('Uninformed')}
                    </Typography>
                    <br />
                    <Typography variant="subtitle1" fontWeight={'bold'}>
                      NÃO VISTORIADO
                    </Typography>
                    <Typography variant="subtitle2">
                      {undefinedItems.length
                        ? undefinedItems.join(', ')
                        : translate('Uninformed')}
                    </Typography>
                    <br />
                    {inoperative.length > 0 && (
                      <>
                        <Typography
                          variant="subtitle1"
                          fontWeight={'bold'}
                          sx={{ mt: 2 }}
                        >
                          INOPERANTE
                        </Typography>
                        <Grid
                          container
                          sx={{
                            border: '1px solid rgba(0,0,0,0.2)',
                            borderRadius: 2,
                            p: 1,
                            maxHeight: 260,
                            overflowY: 'auto',
                          }}
                        >
                          {inoperative.map((record, idx) => (
                            <Grid item xs={12} key={idx} sx={{ mb: 1 }}>
                              <Grid container>
                                <Grid item xs={6}>
                                  <Typography variant="subtitle2">
                                    <strong>
Item:
</strong>
{' '}
                                    {record.inspection_item?.name ??
                                      translate('Uninformed')}
                                  </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="subtitle2">
                                    <strong>
Justificativa:
</strong>
{' '}
                                    {record.notes ?? translate('Uninformed')}
                                  </Typography>
                                </Grid>
                              </Grid>
                              <Grid container sx={{ mt: 1 }}>
                                <Grid item xs={12}>
                                  <Typography variant="subtitle2">
                                    <strong>
Anexo:
</strong>
                                  </Typography>
                                </Grid>
                                {record.media_path ? (
                                  <Grid item xs={12} sx={{ mt: 1 }}>
                                    <img
                                      src={mountUrlRequest(
                                        `/inspection/get-media/${record.media_path}`
                                      )}
                                      alt="Anexo"
                                      style={{
                                        maxWidth: '100%',
                                        borderRadius: 4,
                                      }}
                                    />
                                  </Grid>
                                ) : (
                                  <Grid item xs={12} sx={{ mt: 1 }}>
                                    <Typography variant="subtitle2">
                                      {translate('Uninformed')}
                                    </Typography>
                                  </Grid>
                                )}
                              </Grid>
                            </Grid>
                          ))}
                        </Grid>
                      </>
                    )}
                  </>
                )
              })()}
            </Grid>
          </Grid>
        ) : (
          <Grid container xs={12} display={'flex'} flexDirection={'row'}>
            {showInViewMore.map((item, index) => {
              if (isDateField(item)) {
                return (
                  <Grid item xs={3} key={index} sx={{ pr: 1, pt: 2 }}>
                    <Typography variant="subtitle1" fontWeight={'bold'}>
                      {getLabel[item]
                        ? translate(getLabel[item])
                        : translate(item)}
                      :
                    </Typography>
                    <Typography variant="subtitle2">
                      {infos[item]?.get()
                        ? dayjs(infos[item]?.get()).format('DD/MM/YYYY HH:mm')
                        : translate('Uninformed')}
                    </Typography>
                  </Grid>
                )
              } else if (item == 'birth_date') {
                return (
                  <Grid item xs={3} key={index} sx={{ pr: 1, pt: 2 }}>
                    <Typography variant="subtitle1" fontWeight={'bold'}>
                      {getLabel[item]
                        ? translate(getLabel[item])
                        : translate(item)}
                      :
                    </Typography>
                    <Typography variant="subtitle2">
                      {infos[item]?.get()
                        ? getLabel[item] === 'Data Inicial' ||
                          getLabel[item] === 'Data Final'
                          ? dayjs(infos[item]?.get()).format(
                              'DD/MM/YYYY HH:mm:ss'
                            )
                          : dayjs(infos[item]?.get()).format('DD/MM/YYYY')
                        : translate('Uninformed')}
                    </Typography>
                  </Grid>
                )
              } else if (codeFields && codeFields.includes(item)) {
                return (
                  <Grid item xs={3} key={index} sx={{ pr: 1, pt: 2 }}>
                    <Typography variant="subtitle1" fontWeight={'bold'}>
                      {getLabel[item]
                        ? translate(getLabel[item])
                        : translate(item[0].toUpperCase() + item.slice(1))}
                      :
                    </Typography>
                    <Typography variant="subtitle2">
                      {infos[item]?.get()
                        ? `[${infos[item].get().code}] ${
                            infos[item].get().name
                          }`
                        : translate('Uninformed')}
                    </Typography>
                  </Grid>
                )
              } else if (Array.isArray(infos[item]?.get())) {
                return (
                  <Grid item xs={3} key={index} sx={{ pr: 1, pt: 2 }}>
                    <Typography variant="subtitle1" fontWeight={'bold'}>
                      {getLabel[item]
                        ? translate(getLabel[item])
                        : translate(item[0].toUpperCase() + item.slice(1))}
                      :
                    </Typography>
                    <Typography variant="subtitle2">
                      {showInViewMore.includes('can_manage_any_agency') &&
                      infos['can_manage_any_agency'].get()
                        ? translate('all')
                        : infos[item]?.get().length
                        ? infos[item]
                            ?.get()
                            .map((info) =>
                              info.name ? info.name : info.representation_name
                            )
                            .join(', ')
                        : translate('Uninformed')}
                    </Typography>
                  </Grid>
                )
              } else {
                const infoDependent = item.split(':')

                return (
                  <Grid item xs={3} key={index} sx={{ pr: 1, pt: 2 }}>
                    <Typography variant="subtitle1" fontWeight={'bold'}>
                      {getLabel[item]
                        ? translate(getLabel[item])
                        : translate(item[0].toUpperCase() + item.slice(1))}
                      :
                    </Typography>
                    <Typography variant="subtitle2">
                      {infoDependent?.length > 1
                        ? infos[infoDependent[0]]?.[infoDependent[1]]?.get()
                          ? infos[infoDependent[0]]?.[infoDependent[1]]?.get()
                          : translate('Uninformed')
                        : booleanField?.includes(item)
                        ? infos[item]?.get()
                          ? 'Sim'
                          : 'Não'
                        : infos[item]?.get()
                        ? infos[item]?.get()
                        : translate('Uninformed')}
                    </Typography>
                  </Grid>
                )
              }
            })}
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  )
}
